import {
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
  useState,
  createContext,
} from "react";
import { useServices } from "./service_provider";
import { IAuthData } from "models/authentication";

export interface AuthContextContent {
  authData?: IAuthData;
  signIn: (path?: string) => void;
  signOut: (path?: string) => void;
}

export const AuthContext = createContext<AuthContextContent>({
  authData: undefined,
  signIn: () => undefined,
  signOut: () => undefined,
});
export const useAuth = () => useContext(AuthContext);

interface AuthContextProps {
  path: string;
}

export const AuthContextProvider = ({
  path,
  children,
}: PropsWithChildren<AuthContextProps>): ReactElement => {
  const authContext = useContext(AuthContext);
  const [authData, setAuthData] = useState<IAuthData | undefined>(
    authContext.authData,
  );

  const { auth } = useServices();

  useEffect(() => {
    (async () => {
      const response = await auth.fetchAuthData();
      if (response.isSuccessfull()) {
        const user: IAuthData = response.getData();

        user.name =
          user.claims.find((c) => c.type === user.nameClaimType)?.value ||
          "unknown";

        user.roles =
          user.claims
            .filter((c) => c.type === user.roleClaimType)
            .map((c) => c.value) || [];

        if (!user.isAuthenticated) {
          setAuthData({
            isAuthenticated: false,
            claims: [],
            name: "",
            nameClaimType: "",
            roleClaimType: "",
            roles: [],
          });
          signIn(path);
        } else {
          setAuthData(user);
        }
      }
    })();
  }, []);

  const encodedPath = encodeURIComponent(path);

  function signIn(path?: string) {
    if (path) {
      document.location.href = `/account/login?returnUrl=${encodedPath}`;
    } else {
      document.location.href = "/account/login";
    }
  }

  function signOut(path?: string) {
    if (path) {
      document.location.href = `/account/logout?returnUrl=${encodedPath}`;
    } else {
      document.location.href = "/account/logout";
    }
  }

  return (
    <>
      <AuthContext.Provider value={{ authData, signIn, signOut }}>
        {children}
      </AuthContext.Provider>
    </>
  );
};
